import React, {useContext, useEffect, useRef} from "react";
import DatePicker, {registerLocale} from "react-datepicker";
import de from "date-fns/locale/de"; // the locale you want
import setHours from "date-fns/setHours";
import setMinutes from "date-fns/setMinutes";
import {DateTimeContext} from "../contexts/DateTimeContext";
import {setDate} from "../contexts/actions/dateTime/setDate";
import {GlobalContext} from "../contexts/Provider";
import {send} from "../contexts/actions/form/send";
import {setTime} from "../contexts/actions/dateTime/setTime";
import ContactPerson from "./ContactPerson";
import FormTypeSwitcher from "./FormTypeSwitcher";
import {
    checkboxChecked,
    clearErrors,
    emailValid,
    markError,
    required,
    validateEmail
} from "../contexts/actions/form/validate";
import StoreSwitcher from "./StoreSwitcher";
import FormMessage from "./FormMessage";

registerLocale("de", de);

window.privacyUrl = window.privacyUrl || '';

const MeetingForm = (props) => {
    const showProductSpecific = !!(props?.props?.categoryId || props?.props?.productId);

    const form = useRef(null);
    const name = useRef(null);
    const email = useRef(null);
    const phone = useRef(null);
    const city = useRef(null);
    const message = useRef(null);
    const gdpr = useRef(null);
    const modalMessageForm = useRef(null);

    const {
        state: {
            formType,
            store,
            productId,
            categoryId,
            sent,
            sending,
            name: gName,
            email: gEmail,
            phone: gPhone,
            city: gCity,
            message: gMessage
        },
        dispatch: globalDispatch,
    } = useContext(GlobalContext);

    const {
        state: { date, time, times, freeTimes, fetching, members, member },
        dispatch,
    } = useContext(DateTimeContext);

    useEffect(() => {
        if (formType === "meeting") {
            dispatch({ type: "CHANGE_STORE" });
        }
    }, [store, formType]);

    useEffect(() => {
        if (sent && formType === "meeting") {
            window.dataLayer = window.dataLayer || [];
            const dataLayerData = {
                event: "formSubmission",
                formStore: store.title,
                // phone: phone.current.value,
                email: email.current.value,
            };

            if (props?.props?.productId) {
                dataLayerData.formProduct = props?.props?.productId;
            }

            window.dataLayer.push(dataLayerData);

            form.current.reset();
        }
    }, [sent]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (window.grecaptcha === undefined) {
            window.location.reload();
        }

        clearErrors([name, email, phone, city, message, gdpr], { reflow: true });

        let valid = true;
        const firstErrorRefs = [];

        if (!required(name)) {
            markError(name);
            valid = false;
            firstErrorRefs.push(name);
        }
        if (!required(phone)) {
            markError(phone);
            valid = false;
            firstErrorRefs.push(phone);
        }
        if (!emailValid(email, validateEmail)) {
            markError(email);
            valid = false;
            firstErrorRefs.push(email);
        }
        if (!checkboxChecked(gdpr)) {
            markError(gdpr);
            valid = false;
            firstErrorRefs.push(gdpr);
        }
        if (!required(city)) {
            markError(city);
            valid = false;
            firstErrorRefs.push(city);
        }

        if (!date) valid = false;
        if (!time) valid = false;
        if (!member) valid = false;

        if (!valid) {
            const first = firstErrorRefs[0];
            if (first) markError(first, { focus: true, scroll: true });
            return;
        }

        if (valid) {
            const formData = new FormData(form.current);

            formData.append("date", Math.round(date.getTime() / 1000));
            formData.append("time", Math.round(time.getTime() / 1000));
            formData.append("store_id", store.id);
            if (productId) formData.append("product_id", productId);
            if (categoryId) formData.append("category_id", categoryId);
            formData.append("member_id", member.id);

            window.grecaptcha.ready(async function () {
                const token = await window.grecaptcha.execute(window.captchaSiteKey || '6LdPEQMpAAAAAFll-gXz9LEZSAvZ558tn4nWQ8c-', { action: 'submit' });
                formData.append("g-recaptcha-response", token);

                send({
                    formData,
                    url: "/api/meeting-form",
                })(globalDispatch);
            });
        }
    };
    const titleTimeInput = () => {
        if (fetching) {
            return "Suche freie Zeiten..";
        }

        if (freeTimes.length === 0 && date) {
            return "An diesem Tag haben wir keinen freien Termin mehr";
        }

        if (date) {
            return "Wählen Sie eine Uhrzeit";
        }

        return "Sie müssen ein Datum wählen!";
    };
    const onSetDate = (date) => {
        setDate({ date, store, productId, categoryId })(dispatch);
    };
    const onSetTime = (date) => {
        setTime({ date, members, times })(dispatch);
    };

    const isWeekday = (date) => {
        const day = date.getDay();
        return day === 0 || day === 6;
    };

    const isPast = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return today > d;
    };

    const isVacation = (date) => {
        if (!store.vacation_start) {
            return false;
        }
        const vacationStart = new Date(store.vacation_start);
        const vacationEnd = new Date(store.vacation_end);
        return date >= vacationStart && date <= vacationEnd;
    };

    const controlValidDay = (date) => {
        return !isPast(date) && !isWeekday(date) && !isVacation(date);
    }

    const addMonths = (date, countMonths) => {
        const d = new Date(date);
        d.setMonth(d.getMonth() + countMonths);
        return d;
    };

    return (
        <div className={formType === "meeting" ? "block" : "hidden"}>
            <form className={`w-full space-y-6 text-white product-form-placeholder ${sent ? 'hidden' : 'block'}`}
                  method="post"
                  onSubmit={handleSubmit}
                  ref={form}
                  encType="multipart/form-data">

                <div className="relative floating-label">
                    <input
                        ref={name}
                        value={gName}
                        className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                        name="name"
                        type="text"
                        placeholder=" "
                        onChange={(e) => globalDispatch({
                            type: "SET_FIELD",
                            payload: { key: "name", value: e.target.value }
                        })}
                    />
                    <label className="text-neutrals-400">Meno a priezvisko*</label>
                </div>

                <div className="relative floating-label">
                    <input
                        ref={email}
                        value={gEmail}
                        className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                        name="email"
                        type="text"
                        placeholder=" "
                        onChange={(e) => globalDispatch({
                            type: "SET_FIELD",
                            payload: { key: "email", value: e.target.value }
                        })}
                    />
                    <label className="text-neutrals-400">Email*</label>
                </div>

                {showProductSpecific && (
                    <div className="relative floating-label">
                        <input
                            ref={phone}
                            value={gPhone}
                            name="phone"
                            className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                            type="text"
                            placeholder=" "
                            onChange={(e) => globalDispatch({
                                type: "SET_FIELD",
                                payload: { key: "phone", value: e.target.value }
                            })}
                        />
                        <label className="text-neutrals-400">Telefon*</label>
                    </div>
                )}

                {showProductSpecific && (
                    <div className="relative floating-label">
                        <input
                            ref={city}
                            value={gCity}
                            name="city"
                            className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                            type="text"
                            placeholder=" "
                            onChange={(e) => globalDispatch({
                                type: "SET_FIELD",
                                payload: { key: "city", value: e.target.value }
                            })}
                        />
                        <label className="text-neutrals-400">Montageort*</label>
                    </div>
                )}

                <StoreSwitcher />

                {/* Meeting booking disabled for AT version */}
                {/* <FormTypeSwitcher /> */}

                <div className="flex flex-col w-full md:w-1/2">
                    <div className="relative floating-label">
                        <DatePicker
                            selected={date}
                            locale="de"
                            filterDate={controlValidDay}
                            dateFormat="dd. MM. yyyy"
                            className="field field--date"
                            placeholderText="Datum wählen"
                            disabled={fetching}
                            onChange={onSetDate}
                            minDate={new Date()}
                            maxDate={addMonths(new Date(), 1)}
                            showDisabledMonthNavigation
                        />
                        <label className="text-neutrals-400">Datum*</label>
                    </div>

                    <div className="relative floating-label">
                        <DatePicker
                            selected={time}
                            locale="de"
                            onChange={onSetTime}
                            className="field field--time placeholder-white"
                            placeholderText={titleTimeInput()}
                            timeCaption={titleTimeInput()}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={60}
                            dateFormat="p"
                            minTime={setHours(setMinutes(new Date(), 0), 9)}
                            maxTime={setHours(setMinutes(new Date(), 0), 15)}
                            includeTimes={freeTimes}
                            disabled={!date || freeTimes.length === 0 || fetching}
                        />
                        <label className="text-neutrals-400">Uhrzeit*</label>
                    </div>
                </div>

                <ContactPerson />

                <div className="relative floating-label">
                    <textarea
                        name="message"
                        value={gMessage}
                        ref={message}
                        rows={5}
                        className="w-full bg-transparent border-b border-neutrals-700 text-white py-3 focus:outline-none focus:ring-0 focus:border-neutrals-400"
                        placeholder=" "
                        onChange={(e) => globalDispatch({
                            type: "SET_FIELD",
                            payload: { key: "message", value: e.target.value }
                        })}
                    />
                    <label className="text-neutrals-400">Nachricht*</label>
                </div>

                <div className="flex flex-col lg:flex-row items-start justify-between gap-4">
                    <div className="checkbox-container">
                        <label htmlFor="gdpr-meeting" className="flex items-start gap-3 cursor-pointer">
                            <input id="gdpr-meeting" ref={gdpr} type="checkbox" className="mt-1" />
                            <p className="text-12 text-neutrals-400 leading-snug letter-spacing-2per line-height-130">
                                Mit dem Absenden der Anfrage stimme ich der{" "}
                                <a target="_blank" href={window.privacyUrl} className="underline">Verarbeitung personenbezogener
                                    Daten</a> {" "}
                                durch Profirol s.r.o. gemäß den Datenschutzbestimmungen zu.
                            </p>
                        </label>
                    </div>

                    <button type="submit"
                            className="form-btn self-start w-full lg:w-auto hover:bg-neutrals-500 transition duration-200 hover:text-white disabled:opacity-60"
                            disabled={sending}>
                        {sending ? "Sende..." : "Senden"}
                    </button>
                </div>
            </form>

            {sent ? (
                    <FormMessage message="Vielen Dank für Ihre Nachricht, wir freuen uns auf unser gemeinsames Treffen.">
                        <ContactPerson />
                    </FormMessage>
                )
                : null
            }
        </div>
    );
};

export default MeetingForm;
